import React, { useState, useEffect } from "react";
import ValetApi from "../api/Api";
import { asArray } from "../utility/asArray";
import AccessAssignments from "./AccessAssignments"; // <-- add import

const UserManagement = () => {
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState("");
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [podiums, setPodiums] = useState([]);
    const [selectedPodium, setSelectedPodium] = useState("");
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedUserDetail, setSelectedUserDetail] = useState(null);
    const [showAccess, setShowAccess] = useState(false); // <-- add

    // Fetch regions on mount
    useEffect(() => {
        let cancelled = false;
        async function fetchRegions() {
            try {
                const res = await ValetApi.getAllRegions();
                console.log("getAllRegions:", res?.data);
                if (!cancelled) setRegions(asArray(res?.data?.regions));
            } catch (err) {
                console.error("UserManagement.getAllRegions error:", err);
                if (!cancelled) setRegions([]);
            }
        }
        fetchRegions();
        return () => {
            cancelled = true;
        };
    }, []);

    // Fetch locations when region changes
    useEffect(() => {
        let cancelled = false;
        async function fetchLocations() {
            try {
                const res = await ValetApi.getLocationsByRegion(selectedRegion);
                console.log("getLocationsByRegion:", selectedRegion, res?.data);
                if (!cancelled) setLocations(asArray(res?.data?.locations));
            } catch (err) {
                console.error("UserManagement.getLocationsByRegion error:", err);
                if (!cancelled) setLocations([]);
            }
        }
        if (selectedRegion) fetchLocations();
        else setLocations([]);
        setSelectedLocation("");
        return () => {
            cancelled = true;
        };
    }, [selectedRegion]);

    // Fetch podiums when location changes
    useEffect(() => {
        let cancelled = false;
        async function fetchPodiums() {
            try {
                const res = await ValetApi.getPodiumsByLocation(selectedLocation);
                console.log("getPodiumsByLocation:", selectedLocation, res?.data);
                if (!cancelled) setPodiums(asArray(res?.data?.podiums));
            } catch (err) {
                console.error("UserManagement.getPodiumsByLocation error:", err);
                if (!cancelled) setPodiums([]);
            }
        }
        if (selectedLocation) fetchPodiums();
        else setPodiums([]);
        setSelectedPodium("");
        return () => {
            cancelled = true;
        };
    }, [selectedLocation]);

    // Fetch users when podium changes
    useEffect(() => {
        let cancelled = false;
        async function fetchUsers() {
            try {
                const res = await ValetApi.getUsersByPodium(selectedPodium);
                console.log("getUsersByPodium:", selectedPodium, res?.data);
                if (!cancelled) setUsers(asArray(res?.data?.users));
            } catch (err) {
                console.error("UserManagement.getUsersByPodium error:", err);
                if (!cancelled) setUsers([]);
            }
        }
        if (selectedPodium) fetchUsers();
        else setUsers([]);
        setSelectedUser("");
        return () => {
            cancelled = true;
        };
    }, [selectedPodium]);

    // Fetch user detail when user changes
    useEffect(() => {
        if (!selectedUser) {
            setSelectedUserDetail(null);
            return;
        }
        const list = users || [];
        const u = list.find((u) => String(u.id) === String(selectedUser)) || null;
        setSelectedUserDetail(u);
    }, [selectedUser, users]);

    async function handleDisableUser() {
        const userId = selectedUserDetail?.id;
        if (!userId) return;

        const ok = window.confirm("Disable this user and remove all access?");
        if (!ok) return;

        try {
            await ValetApi.disableUser(userId);
            alert("User disabled.");

            setUsers((list) => (list || []).filter((u) => String(u.id) !== String(userId)));
            setSelectedUser("");
            setSelectedUserDetail(null);
            setShowAccess(false);
        } catch (err) {
            console.error("UserManagement.disableUser error:", err);
            alert(err?.[0] || "Disable failed. See console.");
        }
    }

    // Handler to update user role only
    async function handleUpdateUser(e) {
        e.preventDefault();

        const username = selectedUserDetail?.username;
        if (!username) {
            console.error("UserManagement.updateUser error: missing selectedUserDetail.username");
            alert("No username found on selected user. See console.");
            return;
        }

        const data = {};
        if (selectedUserDetail?.roleId != null) data.roleId = Number(selectedUserDetail.roleId);

        try {
            console.log("UserManagement.updateUser payload:", { username, data });
            const res = await ValetApi.updateUser(username, data);
            console.log("UserManagement.updateUser success:", res?.data);

            setUsers((list) => (list || []).map((u) => (String(u.id) === String(selectedUser) ? { ...u, ...data } : u)));
            setSelectedUserDetail((prev) => (prev ? { ...prev, ...data } : prev));

            alert("User updated!");
        } catch (err) {
            console.error("UserManagement.updateUser error:", err);
            alert("Update failed. See console for details.");
        }
    }

    return (
        <div className="user-management-panel">
            <h3>User Management</h3>
            <form
                className="user-management-form"
                onSubmit={handleUpdateUser}>
                <label className="user-management-field">
                    <span>Region</span>
                    <select
                        className="user-management-select"
                        value={selectedRegion}
                        onChange={(e) => {
                            const v = e.target.value;
                            setSelectedRegion(v ? Number(v) : "");
                        }}>
                        <option value="">Select Region</option>
                        {(regions || []).map((r) => (
                            <option
                                key={r.id}
                                value={r.id}>
                                {r.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="user-management-field">
                    <span>Location</span>
                    <select
                        className="user-management-select"
                        value={selectedLocation}
                        onChange={(e) => {
                            const v = e.target.value;
                            setSelectedLocation(v ? Number(v) : "");
                        }}
                        disabled={!selectedRegion}>
                        <option value="">Select Location</option>
                        {(locations || []).map((l) => (
                            <option
                                key={l.id}
                                value={l.id}>
                                {l.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="user-management-field">
                    <span>Podium</span>
                    <select
                        className="user-management-select"
                        value={selectedPodium}
                        onChange={(e) => {
                            const v = e.target.value;
                            setSelectedPodium(v ? Number(v) : "");
                        }}
                        disabled={!selectedLocation}>
                        <option value="">Select Podium</option>
                        {(podiums || []).map((p) => (
                            <option
                                key={p.id}
                                value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="user-management-field">
                    <span>User</span>
                    <select
                        className="user-management-select"
                        value={selectedUser}
                        onChange={(e) => {
                            const v = e.target.value;
                            setSelectedUser(v ? Number(v) : "");
                        }}
                        disabled={!selectedPodium}>
                        <option value="">Select User</option>
                        {(users || []).map((u) => (
                            <option
                                key={u.id}
                                value={u.id}>
                                {u.firstName} {u.lastName}
                            </option>
                        ))}
                    </select>
                </label>

                {selectedUserDetail && (
                    <>
                        {/* Role update form */}
                        <label className="user-management-field">
                            <span>Update Role</span>
                            <select
                                className="user-management-select"
                                value={selectedUserDetail.roleId}
                                onChange={(e) => setSelectedUserDetail({ ...selectedUserDetail, roleId: Number(e.target.value) })}>
                                <option value={1}>Valet</option>
                                <option value={2}>Supervisor</option>
                                <option value={3}>Manager</option>
                                <option value={4}>Director</option>
                                <option value={5}>Admin</option>
                            </select>
                        </label>
                        <div className="user-management-actions">
                            <button type="submit">Update Role</button>
                            <button
                                type="button"
                                onClick={() => setShowAccess((s) => !s)}>
                                {showAccess ? "Hide Accessible Assignments" : "Show Accessible Assignments"}
                            </button>
                            <button
                                type="button"
                                className="user-management-btn-danger"
                                onClick={handleDisableUser}>
                                Disable User
                            </button>
                        </div>
                    </>
                )}
            </form>

            {/* Access management (separate section) */}
            {showAccess && selectedUserDetail && (
                <AccessAssignments
                    userId={selectedUserDetail.id}
                    username={selectedUserDetail.username}
                />
            )}
        </div>
    );
};

export default UserManagement;
